import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HandCoins, Phone, ChevronRight, RefreshCcw, AlertTriangle, Clock, CalendarClock } from 'lucide-react';
import { AgentLayout } from '../../../components/AgentLayout';
import api from '../../../api';

function fmtINR(n) {
  const num = Number(n || 0);
  if (num >= 10000000) return `\u20B9${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `\u20B9${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `\u20B9${(num / 1000).toFixed(1)} K`;
  return `\u20B9${num.toFixed(0)}`;
}

function fmtDay(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); } catch { return d; }
}

const STATUS_LABELS = {
  new_overdue: 'New', contact_attempted: 'Attempted', connected: 'Connected',
  ptp_set: 'PTP Set', partially_paid: 'Part Paid', regularized: 'Regularized',
  settled_closed: 'Settled', dispute_hold: 'Dispute', escalated_supervisor: 'Escalated',
  legal_initiated: 'Legal', written_off: 'Written Off',
};

export default function AgentRecoveryDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, casesRes] = await Promise.all([
        api.get('/recovery/dashboard/summary'),
        api.get('/recovery/cases'),
      ]);
      setSummary(sumRes.data);
      setCases(casesRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const todayStr = new Date().toISOString().split('T')[0];
  const dueToday = cases.filter((c) => c.nextFollowupDate && c.nextFollowupDate <= todayStr).slice(0, 10);

  return (
    <AgentLayout missedPtpCount={summary?.brokenPtpCount || 0}>
      <div className="au-page">
        <div className="au-greeting fu" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="greet-hi">My Recovery</div>
            <div className="greet-dt">{cases.length} active case{cases.length !== 1 ? 's' : ''}</div>
          </div>
          <button className="ov-gps-btn" onClick={load} style={{ background: 'var(--g1)', color: 'var(--navy)' }} aria-label="Refresh">
            <RefreshCcw size={14} />
          </button>
        </div>

        <div className="au-stats" data-testid="recovery-stats">
          <div className="au-stile fu-1" onClick={() => navigate('/recovery/cases')}>
            <div className="slbl">Active Cases</div>
            <div className="sval">{summary?.totalCases ?? 0}</div>
            <div className="ssub">Your portfolio</div>
          </div>
          <div className="au-stile acc fu-2" onClick={() => navigate('/recovery/cases')}>
            <div className="slbl">Outstanding</div>
            <div className="sval red">{fmtINR(summary?.totalOverdueAmount)}</div>
            <div className="ssub">Total overdue</div>
          </div>
          <div className="au-stile fu-3" onClick={() => navigate('/recovery/cases?status=ptp_set')}>
            <div className="slbl">Missed PTPs</div>
            <div className={`sval ${(summary?.brokenPtpCount || 0) > 0 ? 'red' : ''}`}>{summary?.brokenPtpCount ?? 0}</div>
            <div className="ssub">Promises broken</div>
          </div>
          <div className="au-stile fu-4" onClick={() => navigate('/recovery/cases')}>
            <div className="slbl">Due Today</div>
            <div className="sval">{dueToday.length}</div>
            <div className="ssub">Follow-ups</div>
          </div>
        </div>

        {(summary?.ptpsDueToday?.length || 0) > 0 && (
          <div className="ov-ban fu" style={{ background: '#f59e0b' }}>
            <div className="ov-ico"><Clock size={18} color="#fff" /></div>
            <div className="ov-body">
              <div className="ov-title">
                {summary.ptpsDueToday.length} PTP{summary.ptpsDueToday.length !== 1 ? 's' : ''} due today
              </div>
              {summary.ptpsDueToday.slice(0, 5).map((m) => (
                <div key={m.id} className="ov-row" onClick={() => navigate(`/recovery/cases/${encodeURIComponent(m.loanAccountNumber)}`)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ov-nm">{m.customerName || m.clientId}</div>
                    <div className="ov-phone">Promised {fmtINR(m.promiseAmount)} today</div>
                  </div>
                  <div className="ov-right">
                    <span className="ov-tag">Today</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(summary?.ptpsUpcoming?.length || 0) > 0 && (
          <div className="ov-ban fu" style={{ background: '#0ea5e9' }}>
            <div className="ov-ico"><CalendarClock size={18} color="#fff" /></div>
            <div className="ov-body">
              <div className="ov-title">
                {summary.ptpsUpcoming.length} upcoming PTP{summary.ptpsUpcoming.length !== 1 ? 's' : ''} (next 3 days)
              </div>
              {summary.ptpsUpcoming.slice(0, 5).map((m) => (
                <div key={m.id} className="ov-row" onClick={() => navigate(`/recovery/cases/${encodeURIComponent(m.loanAccountNumber)}`)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ov-nm">{m.customerName || m.clientId}</div>
                    <div className="ov-phone">{fmtINR(m.promiseAmount)} by {fmtDay(m.promiseDate)}</div>
                  </div>
                  <div className="ov-right">
                    <span className="ov-tag">{m.daysUntil}d</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(summary?.missedPromises?.length || 0) > 0 && (
          <div className="ov-ban fu" style={{ background: 'var(--red)' }}>
            <div className="ov-ico"><AlertTriangle size={18} color="#fff" /></div>
            <div className="ov-body">
              <div className="ov-title">
                {summary.missedPromises.length} missed promise{summary.missedPromises.length !== 1 ? 's' : ''}
              </div>
              {summary.missedPromises.slice(0, 3).map((m) => (
                <div key={m.id} className="ov-row" onClick={() => navigate(`/recovery/cases/${encodeURIComponent(m.loanAccountNumber)}`)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ov-nm">{m.customerName || m.clientId}</div>
                    <div className="ov-phone">Promised {fmtINR(m.promiseAmount)} by {m.promiseDate}</div>
                  </div>
                  <div className="ov-right">
                    <span className="ov-tag">{m.daysOverdue}d</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="sec-hd fu">
          <span className="sec-t"><HandCoins size={13} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Due Today / Overdue</span>
          <span className="sec-l" onClick={() => navigate('/recovery/cases')}>See all →</span>
        </div>

        {loading ? (
          <div className="empty-state fu">Loading…</div>
        ) : dueToday.length === 0 ? (
          <div className="empty-state fu">
            <div style={{ fontSize: '32px' }}>🎯</div>
            <div className="empty-title">All caught up</div>
            <div className="empty-text">No cases due for follow-up today.</div>
          </div>
        ) : dueToday.map((c, i) => (
          <CaseCard key={c.id} c={c} idx={i} onOpen={() => navigate(`/recovery/cases/${encodeURIComponent(c.loanAccountNumber)}`)} />
        ))}

        <div style={{ height: '20px' }} />
      </div>
    </AgentLayout>
  );
}

export function CaseCard({ c, onOpen, idx = 0 }) {
  const todayStr = new Date().toISOString().split('T')[0];
  const isOverdue = c.nextFollowupDate && c.nextFollowupDate < todayStr;
  return (
    <div className={`mcard fu-${Math.min(5, idx + 1)}`} onClick={onOpen} style={{ cursor: 'pointer' }}>
      <div className="mc-body">
        <div className="mc-top">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mc-nm">{c.customerName || c.clientId}</div>
            <div className="mc-ow">{c.loanAccountNumber}</div>
          </div>
          <span className={`status-pill ${isOverdue ? 'badge-overdue' : 'badge-today'}`} style={{ fontSize: '10px' }}>
            {STATUS_LABELS[c.status] || c.status}
          </span>
        </div>
        <div className="mc-meta">
          {c.customerPhone && (
            <a href={`tel:${c.customerPhone}`} className="mc-mi clickable" onClick={(e) => e.stopPropagation()}>
              <Phone size={12} /> {c.customerPhone}
            </a>
          )}
          <span className="mc-mi">DPD {c.loan?.dpdDays ?? 0}d ({c.loan?.dpdBucket || '0'})</span>
        </div>
        <div className="mc-foot">
          <div>
            <div style={{ fontSize: '10px', color: 'var(--g3)' }}>OVERDUE</div>
            <div style={{ fontWeight: 700, color: 'var(--red)', fontSize: '13px' }}>{fmtINR(c.loan?.totalOverdueAmount)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '10px', color: 'var(--g3)' }}>FOLLOW-UP</div>
            <div style={{ fontWeight: 700, color: isOverdue ? 'var(--red)' : 'var(--navy)', fontSize: '12px' }}>
              {fmtDay(c.nextFollowupDate)}
            </div>
          </div>
          <ChevronRight size={18} color="var(--g3)" />
        </div>
      </div>
    </div>
  );
}
